import type { SchoolDto } from '../../schools/api/schools.api';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { CodSeLaAprobare } from './CodSeLaAprobare';

interface FormPreviewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    formData: SchoolDto | null;
    /** Codul SE al randului din grila (pe cererile in asteptare: al scolii gasite dupa SIIIR). */
    codSeRand?: string | null;
    isLoading: boolean;
}

export function FormPreviewDialog({
    isOpen,
    onClose,
    formData,
    codSeRand,
    isLoading
}: FormPreviewDialogProps) {
    if (!formData) return null;

    const formatTimestamp = (timestamp?: number) => {
        if (!timestamp) return '-';
        return new Date(timestamp * 1000).toLocaleString('ro-RO');
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Previzualizare Formular de Înscriere</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <span className="text-small text-gray-500">Se încarcă...</span>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* School Information */}
                        <div>
                            <h3 className="ecotic-dialog-section-title">
                                Informații Școală
                            </h3>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="text-small font-medium text-gray-700">Nume Instituție:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.numeInstitutieInvatamant || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">CUI:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.cui || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Cod SIIIR:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.codSiiirUnitate || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Cod SE:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.codSe || codSeRand || <CodSeLaAprobare />}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Telefon:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.telefon || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Număr Elevi:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.numarElevi || '-'}</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-small font-medium text-gray-700">Reprezentant Legal:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.numeReprezentatLegal || '-'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Address */}
                        <div>
                            <h3 className="ecotic-dialog-section-title">
                                Adresă
                            </h3>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="text-small font-medium text-gray-700">Județ:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.adresa?.judet || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Oraș:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.adresa?.oras || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Comună:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.adresa?.comuna || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Sat:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.adresa?.sat || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Stradă:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.adresa?.strada || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-small font-medium text-gray-700">Număr:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.adresa?.numar || '-'}</p>
                                </div>
                                {/* Bloc / scara / etaj / apartament nu se mai cer (decizie owner, 18.09). */}
                                <div>
                                    <label className="text-small font-medium text-gray-700">Cod Poștal:</label>
                                    <p className="text-medium break-words text-gray-900">{formData.adresa?.codPostal || '-'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Professor Information - Commented out: SchoolDto has professorId, not professor object */}
                        {/* TODO: Fetch professor details separately using professorId if needed */}
                        {/* {formData.professor && (
                            <div>
                                <h3 className="ecotic-dialog-section-title">
                                    Informații Profesor
                                </h3>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Nume:</label>
                                        <p className="text-medium break-words text-gray-900">{formData.professor.nume || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Email:</label>
                                        <p className="text-medium break-words text-gray-900">{formData.professor.email || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Telefon:</label>
                                        <p className="text-medium break-words text-gray-900">{formData.professor.telefon || '-'}</p>
                                    </div>
                                </div>
                            </div>
                        )} */}

                        {/* Timestamps */}
                        <div>
                            <h3 className="ecotic-dialog-section-title">
                                Informații Sistem
                            </h3>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="text-small font-medium text-gray-700">Data Actualizare:</label>
                                    <p className="text-medium break-words text-gray-900">{formatTimestamp(formData.updatedAt ? Number(formData.updatedAt) : undefined)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="ecotic-dialog-footer">
                            <button
                                onClick={onClose}
                                className="flex h-11 items-center rounded-md border border-input bg-white px-4 text-small font-medium text-gray-700 hover:bg-gray-50 md:h-9"
                            >
                                Închide
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
